import { useEffect, useState, useRef, useMemo } from 'react';
import { t } from 'i18next';
import cuepointsPlugin from '../plugins/cuepoints';
import { useMediaQuery } from 'react-responsive';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import 'video.js/dist/video-js.css';
import '@videojs/themes/dist/city/index.css';
import { createRoot, Root } from 'react-dom/client';
import { ChaptersOverlayComponent } from './ChaptersOverlayComponent';
import styled, { keyframes, ThemeProvider } from 'styled-components';
import { radius } from '@libertify/ui';
import { Typography } from 'antd';
import { CustomTheme, DEFAULT_PLAYBACK_RATES } from '../types';

import { FullscreenOutlined } from '@ant-design/icons';
import { hasChat, hasDocumentAnimation, videoOnly } from '../helpers';
const VIDEO_OFFSET = 20;

export const VideoPlayer = (props: any) => {
  const {
    options,
    onReady,
    cuepoints,
    highlights,
    sections,
    publication,
    isLoading,
    isCreatorMode,
    onVideoClick,
    pipVideoSize = 'small',
  } = props;

  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const highlightsRef = useRef(highlights);
  useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);
  const isVideoOnly = videoOnly(publication);
  const isMobilePortrait = useMediaQuery({ query: '(max-width: 767px)' });
  const isMobileLandscape = useMediaQuery({
    query: '(max-width: 1000px) and (orientation: landscape)',
  });
  const showDocumentAnimation = hasDocumentAnimation(publication);
  const containsChat = hasChat(publication);
  const isMobileAndVideoCuepointsEnabled =
    showDocumentAnimation && (isMobilePortrait || isMobileLandscape);
  const mobileVideoOnly =
    !showDocumentAnimation && (isMobilePortrait || isMobileLandscape);
  const [isScaled, setIsScaled] = useState(pipVideoSize === 'large');
  const [playerReady, setPlayerReady] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [lastTapTime, setLastTapTime] = useState<number>(0);
  const [showScaleHint, setShowScaleHint] = useState(false);

  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const [startPosition, setStartPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Export SVG play icon and place it on the button
  useEffect(() => {
    fetch('/play-button-blue-pink.svg')
      .then((response) => response.text())
      .then((svg) => {
        let buttonDiv;
        if (isMobilePortrait || isMobileLandscape) {
          buttonDiv = document.querySelector<HTMLElement>(
            '.mobile-play-button',
          );
          const defaultControlButton = document.querySelector<HTMLElement>(
            '.vjs-icon-placeholder.vjs-svg-icon',
          );
          if (defaultControlButton) {
            defaultControlButton.style.display = 'none';
          }
        } else {
          buttonDiv = document.querySelector<HTMLElement>(
            '.vjs-icon-placeholder.vjs-svg-icon',
          );
        }
        if (buttonDiv) {
          buttonDiv.innerHTML = svg;
        }
      });
  }, [isMobilePortrait, isMobileLandscape]);

  // Create and dispose the Video.js player inside the same effect so that
  // React Strict Mode's mount → unmount → remount cycle produces exactly
  // one live player at a time.  Without the cleanup, each mount appends a
  // new <video-js> element and creates a new player while the previous one
  // lingers — leaving analytics listeners on an orphaned instance.
  //
  // dispose() is deferred via queueMicrotask so it runs outside React's
  // commit phase.  Video.js dispose() synchronously removes DOM nodes that
  // may host React roots (bridge components), and removing them during
  // commit causes "Cannot unmount a root while React is rendering" errors.
  useEffect(() => {
    if (!videoRef.current) return;

    // Guards the ready callback below: if this effect is cleaned up (player
    // torn down and replaced) before Video.js's async ready callback fires,
    // the stale callback must not touch state for the player that replaced it.
    let isCancelled = false;

    const videoElement = document.createElement('video-js');
    videoElement.setAttribute('playsInline', 'true');

    videoElement.classList.add('vjs-theme-city');
    if (!isCreatorMode) {
      videoElement.classList.add('vjs-theme-lify');
    }
    if (!isMobilePortrait && !isMobileLandscape) {
      videoElement.classList.add('vjs-theme-desktop');
    }
    // When the playback-rate button is disabled (SCORM skip-lock), lock the
    // available rates to 1x so the speed menu can't reappear via data-setup —
    // otherwise a learner could finish faster than the required viewing time.
    const playbackRatesEnabled =
      options?.controlBar?.playbackRateMenuButton !== false;
    videoElement.setAttribute(
      'data-setup',
      JSON.stringify({
        playbackRates: playbackRatesEnabled ? DEFAULT_PLAYBACK_RATES : [1],
      }),
    );
    videoRef.current.appendChild(videoElement);
    if (!videojs.getPlugin('cuepoints')) {
      videojs.registerPlugin('cuepoints', cuepointsPlugin);
    }
    const player = videojs(videoElement, options, () => {
      if (isCancelled) return;

      videojs.log('player is ready');
      onReady?.(player);

      (player as any).cuepoints();
      setPlayerReady(true);
      // Always mounted; SCORM no-seek mode hides it via .vjs-no-seek CSS so
      // it can reappear once the lesson completes and seeking unlocks.
      (player as any).controlBar.addChild('ChaptersOverlayVjsBridge', {
        sections,
      });

      player.on('loadedmetadata', () => {
        const height = player.el()?.getBoundingClientRect()?.height;
        if (height) {
          setPosition({
            top: window.innerHeight - height - VIDEO_OFFSET,
            left: VIDEO_OFFSET,
          });
        }
      });
    });
    playerRef.current = player;

    return () => {
      isCancelled = true;
      playerRef.current = null;
      setPlayerReady(false);
      if (!player.isDisposed()) {
        // Defer disposal so React finishes its commit phase before
        // Video.js removes DOM nodes hosting bridge React roots.
        queueMicrotask(() => {
          if (!player.isDisposed()) {
            player.dispose();
          }
        });
      }
    };
  }, [options, videoRef]);

  // Hide the play overlay once the user starts playback.
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) return;
    const onPlay = () => setHasPlayed(true);
    player.on('play', onPlay);
    return () => {
      if (!player.isDisposed()) player.off('play', onPlay);
    };
  }, [playerReady]);

  const handlePlayOverlayClick = () => {
    const player = playerRef.current;
    if (!player) return;
    const result = player.play();
    if (result && typeof (result as Promise<void>).catch === 'function') {
      (result as Promise<void>).catch(() => undefined);
    }
  };

  // Update chapters overlay when sections arrive after player init
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady) return;

    const chaptersOverlay = (player as any).controlBar.getChild(
      'ChaptersOverlayVjsBridge',
    );
    if (chaptersOverlay) {
      chaptersOverlay.sections = sections;
      queueMicrotask(() => chaptersOverlay.mountReactComponent());
    }
  }, [sections, playerReady]);

  // Register cuepoints when they load (separate from player init so
  // async-loaded cuepoints are picked up and highlights are always fresh
  // via highlightsRef instead of a stale closure).
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !playerReady || cuepoints.length === 0) return;

    cuepoints.forEach((cuepoint: any, index: number) => {
      (player as any).addCuepoint({
        namespace: 'cuepoints',
        start: cuepoint.start,
        end: cuepoint.end,
        onStart: function (params: any) {
          if (params.error) {
            console.error('Error at second', cuepoint.start);
          } else {
            player.trigger('cuepoints:start', {
              start: cuepoint.start,
              index,
              cuepoint,
              highlights: highlightsRef.current,
            });
          }
        },
        onEnd: function (params: any) {
          player.trigger('cuepoints:end', {
            end: cuepoint.end,
            index,
            cuepoint,
            highlights: highlightsRef.current,
          });
        },
        params: { error: false },
      });
    });
  }, [cuepoints, playerReady]);

  // Player disposal is handled by the creation effect's cleanup above.
  // No separate dispose effect needed.

  useEffect(() => {
    const showTimeout = setTimeout(() => setShowScaleHint(true), 2000);
    const hideTimeout = setTimeout(() => setShowScaleHint(false), 6000);
    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, []);

  // Control scale effect
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const handleTouchStart = (e: TouchEvent) => {
        if (isVideoOnly) {
          onVideoClick?.();
        }
        const now = Date.now();
        const timeSinceLastTap = now - lastTapTime;
        if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
          // Double-tap detected
          setIsScaled((prev) => !prev);
        }
        setLastTapTime(now);
        const touch = e.touches[0];
        if (!touch) return;
        const rect = videoElement.getBoundingClientRect();
        setStartPosition({
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        });
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault(); // Prevent default scrolling
        if (startPosition) {
          const touch = e.touches[0];
          if (!touch) return;
          const videoRect = videoElement.getBoundingClientRect();
          // Get viewport dimensions
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const scale = isScaled ? 2 : 1;
          // Calculate new position
          const newLeft = touch.clientX - startPosition.x / scale;
          const newTop = touch.clientY - startPosition.y / scale;
          // Constrain the position within the viewport
          const videoLeftPosition = Math.max(
            0,
            Math.min(newLeft, viewportWidth - videoRect.width / scale),
          );
          const videoTopPosition = Math.max(
            0, // Ensure the video doesn't go above the viewport
            Math.min(newTop, viewportHeight - videoRect.height / scale),
          );
          setPosition({ top: videoTopPosition, left: videoLeftPosition });
        }
      };
      const handleTouchEnd = () => {
        setStartPosition(null);
      };
      videoElement.addEventListener('touchstart', handleTouchStart, {
        passive: false, // allow to use preventDefault on touching events
      });
      videoElement.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      videoElement.addEventListener('touchend', handleTouchEnd);
      // Cleanup function to remove event listeners
      return () => {
        videoElement.removeEventListener('touchstart', handleTouchStart);
        videoElement.removeEventListener('touchmove', handleTouchMove);
        videoElement.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [startPosition]);

  const mobileStyles: React.CSSProperties = useMemo(() => {
    // When mobile and document animation
    if ((isMobilePortrait || isMobileLandscape) && showDocumentAnimation) {
      return {
        top: position.top,
        left: position.left,
      };
    }
    return {};
  }, [isMobilePortrait, isMobileLandscape, showDocumentAnimation, position]);
  const styles: React.CSSProperties = {
    ...mobileStyles,
    ...(isCreatorMode ? { position: 'relative' } : {}),
  };

  // Determine the video mode class based on available features
  const getVideoClassName = (): string => {
    if (containsChat && showDocumentAnimation) return 'video-chat-document';
    if (isVideoOnly) return 'video-only';
    if (containsChat) return 'video-chat';
    if (showDocumentAnimation) return 'video-document';
    return '';
  };
  const videoClassName = getVideoClassName();

  // Determine the landscape mode
  const landscapeClassName = isMobileLandscape ? 'landscape' : '';
  const cuepointsClassName = isMobileAndVideoCuepointsEnabled
    ? `movingDiv ${isScaled ? 'expanded' : ''}`
    : '';

  return (
    <StyledVideo
      data-vjs-player
      $mobileVideoOnly={mobileVideoOnly}
      $isVideoLoaded={!isLoading}
      $isCreatorMode={isCreatorMode}
    >
      <div
        ref={videoRef}
        className={`video-player ${videoClassName} ${cuepointsClassName} ${landscapeClassName}`}
        style={styles}
      >
        {isMobileAndVideoCuepointsEnabled && (
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: radius.box,
              backgroundColor: 'rgba(0,0,0,0.7)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              transition: '0.5s ease',
              opacity: showScaleHint ? 1 : 0,
            }}
          >
            <Typography.Text style={{ color: 'white', width: '70%' }}>
              {t('Double-tap to scale')}
              <ScaleIcon />
            </Typography.Text>
          </div>
        )}
        {playerReady && !hasPlayed && (
          <PlayOverlay
            role="button"
            aria-label={t('Play')}
            tabIndex={0}
            onClick={handlePlayOverlayClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePlayOverlayClick();
              }
            }}
          >
            <PlayOverlayButton>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M8 5.14v13.72L19 12 8 5.14z" />
              </svg>
            </PlayOverlayButton>
          </PlayOverlay>
        )}
      </div>
    </StyledVideo>
  );
};

export default VideoPlayer;

// Helper function to extract theme from CSS variables
const extractThemeFromDOM = (): CustomTheme => {
  const rootElement = document.querySelector(':root') as HTMLElement;
  return {
    colors: {
      primary: rootElement?.style.getPropertyValue('--primary-color'),
      ctaTextColor: rootElement?.style.getPropertyValue('--ctaText-color'),
      secondary: rootElement?.style.getPropertyValue('--secondary-color'),
      highlight: rootElement?.style.getPropertyValue('--highlight-color'),
    },
    fontFamily: rootElement?.style.getPropertyValue('--font-family'),
  };
};

const VjsComponent = videojs.getComponent('Component');

class ChaptersOverlayVjsBridge extends VjsComponent {
  sections: any;
  reactRoot: Root | null = null;

  constructor(player: any, options: any) {
    super(player, options);
    // The base Component element is a bare div — tag it so CSS can target
    // the chapters menu (hidden in SCORM no-seek mode via .vjs-no-seek).
    this.addClass('vjs-chapters-overlay');
    this.sections = options.sections;
    this.mountReactComponent = this.mountReactComponent.bind(this);

    player.ready(() => {
      queueMicrotask(() => this.mountReactComponent());
    });

    this.on('dispose', () => {
      const root = this.reactRoot;
      this.reactRoot = null;
      if (root) {
        setTimeout(() => root.unmount(), 0);
      }
    });
  }

  mountReactComponent() {
    if (this.player_.isDisposed?.() || !this.el()) return;
    const theme = extractThemeFromDOM();
    if (!this.reactRoot) {
      this.reactRoot = createRoot(this.el());
    }
    this.reactRoot.render(
      <ThemeProvider theme={theme as any}>
        <ChaptersOverlayComponent
          vjsBridgeComponent={this}
          player={this.player_}
          text=""
          sections={this.sections}
        />
      </ThemeProvider>,
    );
  }
}

// Make sure to register the Video.js component so Video.js knows it exists
videojs.registerComponent('ChaptersOverlayVjsBridge', ChaptersOverlayVjsBridge);

const pulseScaleIcon = keyframes`{
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}`;

const ScaleIcon = styled(FullscreenOutlined)`
  font-size: 30px;
  display: block;
  margin-top: 20px;
  animation: ${pulseScaleIcon} 2s ease 0s 2;
`;

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(0, 0, 0, 0.55);
    outline: none;
  }
`;

const PlayOverlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: ${radius.circle};
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s ease,
    background 0.2s ease;

  svg {
    width: 36px;
    height: 36px;
    fill: #fff;
    margin-left: 4px;
  }

  ${PlayOverlay}:hover &,
  ${PlayOverlay}:focus-visible & {
    transform: scale(1.06);
    background: rgba(255, 255, 255, 0.28);
  }
`;

interface StyledVideoProps extends React.HTMLProps<HTMLDivElement> {
  $mobileVideoOnly: boolean;
  $isVideoLoaded: boolean;
  $isCreatorMode?: boolean;
}

// TODO: remove some unnecessary the styles for the video player
const StyledVideo = styled.div<StyledVideoProps>`
  opacity: ${(props) => (props.$isVideoLoaded ? 1 : 0)};
  .movingDiv {
    position: absolute;
    z-index: 4;
    border-radius: ${radius.box}px;
    transition: transform 0.3s ease;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    overflow: hidden;

    .video-js {
      border-radius: ${radius.box}px; /* for video border, 50 for talking head */
      width: 30vw;
      max-height: 200px;
      overflow: hidden;
    }
  }

  .movingDiv.expanded {
    transform: scale(2);
  }
  ${(props) =>
    !props.$isCreatorMode &&
    `
    @keyframes _iconBtnAnim {
      0% { opacity: 1; }
      10% { opacity: 0; }
      20% { opacity: 1; }
      30% { opacity: 0; }
      40% { opacity: 1; }
    }
    @keyframes _svgColorAnim {
      0% {}
      20% { fill: white; }
      30% {}
      100% {}
    }

    .vjs-big-play-button {
      background: transparent;

      .vjs-svg-icon {
        animation: _iconBtnAnim 2s infinite;
        top: 0;
        width: 80%;
        filter: drop-shadow(0 0 40px rgba(26, 66, 216, 0.6));
      }

      .vjs-svg-icon svg path {
        animation: _svgColorAnim 2s infinite;
      }
    }

    .vjs-theme-city:hover .vjs-big-play-button {
      background: transparent !important;
    }
  `}

  ${(props) =>
    !props.$isCreatorMode &&
    `
  .vjs-control-bar {
    ${
      props.$mobileVideoOnly
        ? `
      z-index: 21;
      height: 100px;
      padding-top: 20px;
      align-items: center;

      .vjs-play-control {
        display: none;
      }
    `
        : ''
    }
  }

  @media (width >= 1001px) and (width <= 1400px) {
    .vjs-control-bar {
      .vjs-playback-rate {
        z-index: 5;
      }
    }
  }
  `}
`;
